import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import CustomSelect from '../ui/CustomSelect'; // Import CustomSelect
import { createBacktestConfig, getBacktestConfig, updateBacktestConfig } from '../../api/botsApi';

const BacktestConfigForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { botId, versionId, configId } = useParams(); // Get IDs from URL

  const [label, setLabel] = useState('Sectioned Builder Backtest');
  const [riskJson, setRiskJson] = useState('{}');
  const [slippageMs, setSlippageMs] = useState('');
  const [slippageR, setSlippageR] = useState('');
  // timeframe removed from config (now on run). Optional default_timeframe retained for convenience.
  const [defaultTimeframe, setDefaultTimeframe] = useState('');
  const [executionConfigId, setExecutionConfigId] = useState('');
  const [scope, setScope] = useState('version'); // 'version' | 'bot' | 'user'
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const timeframeOptions = [
    { value: 'M1', label: '1M' },
    { value: 'M5', label: '5M' },
    { value: 'M15', label: '15M' },
    { value: 'M30', label: '30M' },
    { value: 'H1', label: '1H' },
    { value: 'H4', label: '4H' },
    { value: 'D1', label: '1D' },
  ];

  useEffect(() => {
    // Default scope based on available params
    if (!configId) {
      if (versionId) setScope('version');
      else if (botId) setScope('bot');
      else setScope('user');
    }
  }, [botId, versionId, configId]);

  useEffect(() => {
    if (configId) {
      setIsEditMode(true);
      setIsLoading(true);
      getBacktestConfig(configId)
        .then(data => {
          setLabel(data.label || '');
          setRiskJson(JSON.stringify(data.risk_json ?? {}, null, 2));
          setSlippageMs(data.slippage_ms ?? '');
          setSlippageR(data.slippage_r ?? '');
          setDefaultTimeframe(data.default_timeframe || data.timeframe || '');
          setExecutionConfigId(data.execution_config_id || '');
          // Infer scope from existing data
          if (data.bot_version) setScope('version');
          else if (data.bot) setScope('bot');
          else setScope('user');
        })
        .catch(err => {
          console.error('Failed to fetch backtest config for editing:', err);
          setError(err.message || 'Failed to load configuration for editing.');
        })
        .finally(() => setIsLoading(false));
    }
  }, [configId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    let parsedRiskJson;
    try {
      parsedRiskJson = JSON.parse(riskJson || '{}');
    } catch (jsonError) {
      setError('Invalid JSON in Risk JSON field. Please correct it.');
      setIsLoading(false);
      return;
    }

    const base = {
      label,
      risk_json: parsedRiskJson,
      slippage_ms: slippageMs === '' ? undefined : parseInt(slippageMs, 10),
      slippage_r: slippageR === '' ? undefined : parseFloat(slippageR),
  // New contract: spec required (placeholder minimal) - keep backward compat if not provided by caller.
  // For now, risk_json still passed; actual strategy spec not handled by this lightweight form.
  default_timeframe: defaultTimeframe || undefined,
      execution_config_id: executionConfigId || undefined,
    };

    const payload = { ...base };
    if (scope === 'version') payload.bot_version = versionId;
    else if (scope === 'bot') payload.bot_id = botId;
    // user scope: omit both

    try {
      if (isEditMode) {
        await updateBacktestConfig(configId, payload);
      } else {
        await createBacktestConfig(payload);
      }
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/bots/${botId}/versions/${versionId}`);
      }
    } catch (err) {
      console.error('Failed to save backtest configuration:', err);
      setError(err.message || `Failed to save configuration. ${err.detail || ''}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isEditMode && isLoading && !error) {
    return <p>Loading configuration for editing...</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <p className="text-destructive">{error}</p>}

      {/* Scope Selection */}
      <div className="grid grid-cols-3 gap-2">
        <label className="text-sm font-medium text-textPrimary col-span-3">Scope</label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="scope"
            value="version"
            checked={scope === 'version'}
            onChange={() => setScope('version')}
            disabled={!versionId}
          />
          <span>Version</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="scope"
            value="bot"
            checked={scope === 'bot'}
            onChange={() => setScope('bot')}
            disabled={!botId}
          />
          <span>Bot</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="scope"
            value="user"
            checked={scope === 'user'}
            onChange={() => setScope('user')}
          />
          <span>My</span>
        </label>
      </div>

      <div>
        <label htmlFor="label" className="block text-sm font-medium text-textPrimary">Label</label>
        <Input id="label" type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., Aggressive EURUSD M5" required className="mt-1" />
      </div>

      {/* Hidden risk JSON editor, still part of payload */}
      <div hidden>
        <label htmlFor="riskJson" className="block text-sm font-medium text-textPrimary">Risk Configuration (JSON)</label>
        <textarea
          id="riskJson"
          value={riskJson}
          onChange={(e) => setRiskJson(e.target.value)}
          placeholder='{ "max_drawdown_percent": 0.05, "risk_per_trade_percent": 0.01 }'
          className="mt-1 block w-full rounded-md border-borderDefault shadow-sm focus:border-primary focus:ring-primary sm:text-sm bg-backgroundSurface text-textPrimary p-2 h-32 font-mono"
        />
      </div>

      <div>
        <label htmlFor="executionConfigId" className="block text-sm font-medium text-textPrimary">Execution Config ID (optional)</label>
        <Input id="executionConfigId" type="text" value={executionConfigId} onChange={(e) => setExecutionConfigId(e.target.value)} placeholder="UUID" className="mt-1" />
      </div>

      <div>
        <label htmlFor="slippageMs" className="block text-sm font-medium text-textPrimary">Slippage (ms)</label>
        <Input id="slippageMs" type="number" value={slippageMs} onChange={(e) => setSlippageMs(e.target.value)} placeholder="e.g., 500" required className="mt-1" />
      </div>

      <div>
        <label htmlFor="defaultTimeframe" className="block text-sm font-medium text-textPrimary">Default Timeframe (optional)</label>
        <CustomSelect id="defaultTimeframe" options={timeframeOptions} value={defaultTimeframe} onChange={setDefaultTimeframe} placeholder="Select a default timeframe" className="mt-1" />
        <p className="mt-1 text-[11px] text-textSecondary">Timeframe now selected when launching runs. This sets an optional suggested default.</p>
      </div>

      <div>
        <label htmlFor="slippageR" className="block text-sm font-medium text-textPrimary">Slippage (R - multiple of ATR or similar risk unit)</label>
        <Input id="slippageR" type="number" step="0.01" value={slippageR} onChange={(e) => setSlippageR(e.target.value)} placeholder="e.g., 0.5" required className="mt-1" />
      </div>

      <div className="flex justify-end space-x-3">
        <Button type="button" variant="secondary" onClick={() => navigate(`/bots/${botId}/versions/${versionId}`)}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={isLoading}>{isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Configuration' : 'Create Configuration')}</Button>
      </div>
    </form>
  );
};

export default BacktestConfigForm;
